/**
 * Checkout page — in-clinic checkout queue for today's appointments.
 *
 * Shows patients who have an appointment today at the selected clinic and have
 * at least one actionable order (needs preparation, payment, or collection).
 */
import { X } from 'lucide-react'
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { useMemo, useState } from 'react'

import { clinicIdSchema } from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { SearchInput } from '@/frontend/components/search-input'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent } from '@/frontend/components/ui/dialog'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { CheckoutScreen } from '@/frontend/features/orders/checkout-screen/checkout-screen'
import { useCheckoutQueue } from '@/frontend/features/orders/hooks/use-checkout-queue'
import { groupOrders } from '@/frontend/features/orders/orders.utils'
import { PatientOrderCard } from '@/frontend/features/orders/patient-order-card'
import { useDebounce } from '@/frontend/hooks/use-debounce'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'

export function CheckoutPage() {
  useDocumentTitle('Checkout')
  const [clinicFilter, setClinicFilter] = useQueryState(
    'clinic',
    parseAsStringLiteral(clinicIdSchema.options)
  )
  const [patientSearch, setPatientSearch] = useQueryState('patient', parseAsString)
  const [checkoutPatientId, setCheckoutPatientId] = useState<string | null>(null)

  const debouncedSearch = useDebounce(patientSearch, 300)
  const q = debouncedSearch || undefined

  const {
    orders: checkoutOrders,
    isLoading,
    error,
  } = useCheckoutQueue({
    clinicId: clinicFilter ?? undefined,
    q,
  })

  const groups = useMemo(() => groupOrders(checkoutOrders ?? []), [checkoutOrders])

  const showClear = !!patientSearch

  if (error) return <div>Error: {error.message}</div>

  return (
    <div>
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-headline-lg">Checkout</h1>
        <p className="mt-1 text-muted-foreground">In-clinic checkout for today's appointments.</p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-3">
        <ClinicSearchWithDefault
          id="filter-clinic"
          value={clinicFilter}
          onChange={setClinicFilter}
          placeholder="All Clinics"
        />
        <SearchInput
          placeholder="Search patient or medication..."
          value={patientSearch ?? ''}
          onChange={(v) => setPatientSearch(v || null)}
        />
        {showClear && (
          <Button
            variant="text"
            onClick={() => {
              setPatientSearch(null)
            }}
          >
            <X className="h-3 w-3" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Patient list */}
      {!clinicFilter ? (
        <p className="py-8 text-center text-muted-foreground">
          Select a clinic to view today's checkout queue.
        </p>
      ) : isLoading ? (
        <CheckoutListSkeleton />
      ) : groups.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          No patients with outstanding orders for today.
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <PatientOrderCard key={group.patientId} group={group} onSelect={setCheckoutPatientId} />
          ))}
        </div>
      )}

      {/* Checkout dialog */}
      <Dialog open={checkoutPatientId !== null} onOpenChange={() => setCheckoutPatientId(null)}>
        <DialogContent className="sm:max-w-7xl p-4">
          {checkoutPatientId && <CheckoutScreen patientId={checkoutPatientId} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function CheckoutListSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeleton cards have no stable key
        <div key={i} className="rounded-lg border p-4 space-y-3 shadow-xs">
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ))}
    </div>
  )
}
